import { createContext, useContext, useState, type FC, type PropsWithChildren } from 'react'

import { useCallback } from 'modules/core/hooks/useCallback'

import type { GraphQLEmployee } from 'modules/org_chart/lib/employeeUtils'
import type { AddPositionNodeData } from 'modules/org_chart/models/NodeData'
import type { PointNode } from 'modules/org_chart/models/PointNode'
import type { TagInfoProp } from 'modules/org_chart/models/TagInfoProp'

type SidePanelTabType = 'reports' | 'profile'

export interface OrgChartStateContextValue {
  hintedNode?: PointNode<AddPositionNodeData> | undefined
  activeEmployee?: GraphQLEmployee
  tagInfoProp: TagInfoProp
  searchResultId?: string
  activeSidePanelTab: SidePanelTabType
  update: (key: ChangeType, value: unknown) => void
}

type ChangeType = Exclude<keyof OrgChartStateContextValue, 'update'>

export const OrgChartStateContext = createContext<OrgChartStateContextValue | null>(null)

export const useOrgChartState = (): OrgChartStateContextValue => {
  const context = useContext(OrgChartStateContext)
  if (context === null) {
    throw new Error('useOrgChartState must be used within an OrgChartStateContext.Provider')
  }
  return context
}

type Props = PropsWithChildren<Record<string, unknown>>

const Provider: FC<Props> = ({ children }) => {
  const [hintedNode, setHintedNode] = useState<PointNode<AddPositionNodeData>>()
  const [activeEmployee, setActiveEmployee] = useState<GraphQLEmployee>()
  const [tagInfoProp, setTagInfoProp] = useState<TagInfoProp>('workplace')
  const [searchResultId, setSearchResultId] = useState<string>()
  const [activeSidePanelTab, setActiveSidePanelTab] = useState<SidePanelTabType>('profile')

  const update = useCallback((key: ChangeType, value: unknown): void => {
    switch (key) {
      case 'hintedNode':
        setHintedNode(value as PointNode<AddPositionNodeData>)
        break
      case 'activeEmployee':
        setActiveEmployee(value as GraphQLEmployee)
        break
      case 'tagInfoProp':
        setTagInfoProp(value as TagInfoProp)
        break
      case 'activeSidePanelTab':
        setActiveSidePanelTab(value as SidePanelTabType)
        break
      case 'searchResultId':
        setSearchResultId(value as string)
        break
      default:
        return
    }
  }, [])

  return (
    <OrgChartStateContext.Provider
      value={{
        hintedNode,
        activeEmployee,
        tagInfoProp,
        searchResultId,
        activeSidePanelTab,
        update,
      }}
    >
      {children}
    </OrgChartStateContext.Provider>
  )
}

export default Provider
